import { Controller } from 'react-hook-form';
import { format } from 'date-fns';
import { Select, Checkbox, DateInputRange, SelectType } from '@ui/src';
import { NotificationItemIssueStatus } from '../../hoc/center/types';
import SidepanelCategoryFilter from './sidepanel-category-filter';
import { FiltersSidepanelTypes } from './types';

import cn from './styles.module.css';

const NotificationCenterFilters = ({
  control,
  users,
  withoutCategories,
  setValue,
  onReset,
  filterFields,
  category,
  currentUser,
}: FiltersSidepanelTypes) => (
  <div className={cn.notificationFilters}>
    <SidepanelCategoryFilter
      setValue={setValue}
      filterFields={filterFields}
      withoutCategories={withoutCategories}
      category={category}
    />
    <div className={cn.filtersBlockTitle}>Status</div>
    <Controller
      control={control}
      name="status"
      render={({ field: { value, name, onChange } }) => (
        <div className={cn.statusesWrapper}>
          {[
            {
              name: `${NotificationItemIssueStatus.TODO.charAt(
                0,
              )}${NotificationItemIssueStatus.TODO.slice(1).toLowerCase()}`,
              value: NotificationItemIssueStatus.TODO,
            },
            {
              name: NotificationItemIssueStatus.IN_PROGRESS.split('_')
                .map(
                  (item) => `${item.charAt(0)}${item.slice(1).toLowerCase()}`,
                )
                .join(' '),
              value: NotificationItemIssueStatus.IN_PROGRESS,
            },
            {
              name: `${NotificationItemIssueStatus.DONE.charAt(
                0,
              )}${NotificationItemIssueStatus.DONE.slice(1).toLowerCase()}`,
              value: NotificationItemIssueStatus.DONE,
            },
            {
              name: `${NotificationItemIssueStatus.CANCELED.charAt(
                0,
              )}${NotificationItemIssueStatus.CANCELED.slice(1).toLowerCase()}`,
              value: NotificationItemIssueStatus.CANCELED,
            },
          ].map((item) => (
            <div className={cn.status} key={item.value}>
              <Checkbox
                name={`${name}-${item.value}`}
                type="checkbox"
                checked={value?.includes(item.value as string)}
                title={item.name}
                onChange={() => {
                  let nextStatuses = ([] as string[]).concat(value);

                  if (nextStatuses?.find((status) => status === item.value)) {
                    nextStatuses = nextStatuses?.filter(
                      (status) => status !== item.value,
                    );
                  } else {
                    nextStatuses.push(item.value);
                  }

                  onChange(nextStatuses);
                }}
              />
            </div>
          ))}
        </div>
      )}
    />
    <div
      className={`${cn.notificationFiltersRow} ${cn.notificationFiltersRowFlex}`}
      style={{ zIndex: 8 }}
    >
      <div className={cn.selectWrapper}>
        <Controller
          control={control}
          name="responsibleId"
          defaultValue={[]}
          render={({ field: { value, name, onChange } }) => (
            <Select
              name={name}
              value={value}
              list={users || []}
              placeholder="Select responsible"
              onChange={onChange}
              title="Responsible"
              mode={SelectType.MULTIPLE}
              withButtonInTitle={
                !filterFields?.responsibleId?.includes(
                  currentUser?.id as string,
                )
              }
              titleButtonText="Assign to me"
              onTitleButtonClick={() => {
                onChange([...value, currentUser?.id]);
              }}
              fullWidth
            />
          )}
        />
      </div>
      <div className={cn.selectWrapper}>
        <Controller
          control={control}
          name="executorId"
          defaultValue={[]}
          render={({ field: { value, name, onChange } }) => (
            <Select
              name={name}
              value={value}
              list={users || []}
              placeholder="Select executor"
              onChange={onChange}
              mode={SelectType.MULTIPLE}
              title="Executor"
              withButtonInTitle={
                !filterFields?.executorId?.includes(currentUser?.id as string)
              }
              titleButtonText="Assign to me"
              onTitleButtonClick={() => {
                onChange([...value, currentUser?.id]);
              }}
              fullWidth
            />
          )}
        />
      </div>
    </div>
    <div className={cn.notificationFiltersRow} style={{ zIndex: 7 }}>
      <Controller
        control={control}
        name="createdAt"
        render={({ field: { value, onChange } }) => (
          <>
            <div className={cn.dateInputTitle}>Create at</div>
            <DateInputRange
              values={value}
              onChangeRange={(date) => {
                onChange(date.map((item) => format(item, 'yyyy-MM-dd')));

                const [from, to] = date;

                if (from) {
                  setValue('createdAtFrom', format(from, 'yyyy-MM-dd'));
                }

                if (to) {
                  setValue('createdAtTo', format(to, 'yyyy-MM-dd'));
                }
              }}
              title="Create at"
              placeholder="Select date"
              className={`${cn.dateInputRangeFilter} ${
                !value?.length ? cn.empty : ''
              }`}
              onReset={() => {
                setValue('createdAtFrom', '');
                setValue('createdAtTo', '');
                setValue('createdAt', []);
              }}
              withoutTwoPickers
              withoutCustomSelector
              withValueReset
            />
          </>
        )}
      />
    </div>
    <div className={cn.notificationFiltersRow} style={{ zIndex: 6 }}>
      <Controller
        control={control}
        name="deadline"
        render={({ field: { value, onChange } }) => (
          <>
            <div className={cn.dateInputTitle}>Deadline</div>
            <DateInputRange
              values={value}
              onChangeRange={(date) => {
                onChange(date.map((item) => format(item, 'yyyy-MM-dd')));

                const [from, to] = date;

                if (from) {
                  setValue('deadlineFrom', format(from, 'yyyy-MM-dd'));
                }

                if (to) {
                  setValue('deadlineTo', format(to, 'yyyy-MM-dd'));
                }
              }}
              placeholder="Select date"
              className={`${cn.dateInputRangeFilter} ${
                !value?.length ? cn.empty : ''
              }`}
              onReset={onReset}
              withoutTwoPickers
              withoutHidePlaceholder
              withoutCustomSelector
              withValueReset
            />
          </>
        )}
      />
    </div>
  </div>
);

export default NotificationCenterFilters;
